import { useEffect, useMemo, useState } from 'react';
import { mutate } from 'swr';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { post, ApiError } from '@/lib/api';
import { ViewZoneCanvas, zoneColor } from './ViewZoneCanvas';
import {
  PRINT_POSITIONS, VIEW_KEYS,
  type ProductConfig, type ProductView, type PrintAreaDef, type ViewArea, type ViewKey,
} from '@/lib/product-config';

// Full visual placement editor for a product config: print areas (the named
// multi-logo slots with print dimensions + $ addons) and per-view logo zones
// drawn directly on the garment images. Replaces the product-config-admin
// click-away — saves through the same POST /api/product-configs upsert.
type Props = {
  open: boolean;
  onClose: () => void;
  product: ProductConfig | null;
  onSaved?: (next: ProductConfig) => void;
};

const inputCls = 'w-full rounded-[8px] border border-black/[0.08] bg-surface px-2 py-1.5 text-[12px] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/15';

function num(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

export function ProductVisualEditor({ open, onClose, product, onSaved }: Props) {
  const [printAreas, setPrintAreas] = useState<PrintAreaDef[]>([]);
  const [views, setViews] = useState<Record<ViewKey, ProductView>>({ front: {}, back: {}, side: {} });
  const [activeView, setActiveView] = useState<ViewKey>('front');
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!product || !open) return;
    // Seed printAreas from the doc; legacy single-printArea docs get one slot
    // derived from the old map so the editor is never empty.
    const fromDoc = Array.isArray(product.printAreas) ? product.printAreas : [];
    const seeded = fromDoc.length > 0 ? fromDoc : (product.printArea?.position ? [{
      id: 'area-1',
      label: product.printArea.positionDisplay || product.printArea.position,
      position: product.printArea.position,
      printWidth: product.printArea.width,
      printHeight: product.printArea.height,
      addon: 0,
    }] : []);
    setPrintAreas(seeded.map((p) => ({ ...p })));
    setViews({
      front: { ...(product.views?.front || {}) },
      back: { ...(product.views?.back || {}) },
      side: { ...(product.views?.side || {}) },
    });
    setActiveView('front');
    setSelectedAreaId(seeded[0]?.id ?? null);
  }, [product, open]);

  const view = views[activeView];
  const imageSrc = view.imageUrl || view.imageDataUrl || '';
  const areas = useMemo(
    () => (Array.isArray(view.areas) ? view.areas.filter((a) => printAreas.some((p) => p.id === a.areaId)) : []),
    [view.areas, printAreas]
  );

  if (!product) return null;

  function patchView(key: ViewKey, patch: Partial<ProductView>) {
    setViews((v) => ({ ...v, [key]: { ...v[key], ...patch } }));
  }

  function addPrintArea() {
    const n = printAreas.length + 1;
    const id = `area-${Date.now().toString(36)}`;
    setPrintAreas((p) => [...p, { id, label: `Area ${n}`, position: 'left-chest', printWidth: 3.2, printHeight: 3.2, addon: 0 }]);
    setSelectedAreaId(id);
  }

  function removePrintArea(id: string) {
    setPrintAreas((p) => p.filter((a) => a.id !== id));
    // Drop its zones from every view.
    setViews((v) => {
      const next = { ...v };
      for (const k of VIEW_KEYS) {
        const va = next[k].areas;
        if (Array.isArray(va)) next[k] = { ...next[k], areas: va.filter((a) => a.areaId !== id) };
      }
      return next;
    });
    if (selectedAreaId === id) setSelectedAreaId(null);
  }

  function patchPrintArea(id: string, patch: Partial<PrintAreaDef>) {
    setPrintAreas((p) => p.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }

  async function save() {
    if (!product) return;
    setSaving(true);
    try {
      const payload: ProductConfig = {
        ...product,
        printAreas,
        views: {
          front: views.front,
          back: views.back,
          side: views.side,
        },
      };
      // Keep the legacy single print-area map in sync with the first area so
      // older readers (mockup pipeline fallbacks) stay coherent.
      if (printAreas[0]) {
        payload.printArea = {
          ...(product.printArea || {}),
          position: printAreas[0].position,
          positionDisplay: printAreas[0].label || printAreas[0].position,
          width: printAreas[0].printWidth,
          height: printAreas[0].printHeight,
        };
      }
      await post('/api/product-configs', { product: payload });
      toast.success('Visual placement saved');
      await mutate('/api/products?limit=500');
      onSaved?.(payload);
      onClose();
    } catch (e) {
      const body = (e as ApiError).body as { message?: string; error?: string } | null;
      toast.error(body?.message || body?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const placedIds = new Set(areas.map((a) => a.areaId));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-h-[88vh] max-w-[880px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Visual placement — {product.displayName || product.id}</DialogTitle>
          <p className="text-[11.5px] text-muted">
            Define print areas, then draw each one onto the garment views. Select an area chip and drag on the image to place it.
          </p>
        </DialogHeader>

        <div className="grid gap-5 md:grid-cols-[300px_1fr]">
          {/* Print areas panel */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Print areas</h3>
              <Button size="sm" variant="secondary" onClick={addPrintArea}>
                <Plus className="h-3.5 w-3.5" aria-hidden /> Add
              </Button>
            </div>
            {printAreas.length === 0 ? (
              <p className="rounded-[10px] border border-dashed border-border p-3 text-[11.5px] text-muted">
                No print areas yet — add one to start placing logos.
              </p>
            ) : (
              <div className="space-y-2.5">
                {printAreas.map((pa) => (
                  <div
                    key={pa.id}
                    className={`rounded-[10px] border p-2.5 transition-colors ${selectedAreaId === pa.id ? 'border-primary/50 bg-primary/[0.04]' : 'border-black/[0.07]'}`}
                    onClick={() => setSelectedAreaId(pa.id)}
                  >
                    <div className="mb-1.5 flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: zoneColor(printAreas, pa.id) }} />
                      <input
                        className="w-full bg-transparent text-[12px] font-medium outline-none"
                        value={pa.label || ''}
                        placeholder="Label"
                        onChange={(e) => patchPrintArea(pa.id, { label: e.target.value })}
                      />
                      <button
                        className="text-subtle transition-colors hover:text-danger"
                        title="Remove print area"
                        onClick={(e) => { e.stopPropagation(); removePrintArea(pa.id); }}
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      <select
                        className={inputCls}
                        value={pa.position || 'left-chest'}
                        onChange={(e) => patchPrintArea(pa.id, { position: e.target.value })}
                      >
                        {[...PRINT_POSITIONS, ...(pa.position && !(PRINT_POSITIONS as readonly string[]).includes(pa.position) ? [pa.position] : [])].map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                      <input
                        className={inputCls}
                        type="number" step="0.5" placeholder="Addon $"
                        value={pa.addon ?? ''}
                        title="Per-item $ addon when this area is decorated"
                        onChange={(e) => patchPrintArea(pa.id, { addon: num(e.target.value) })}
                      />
                      <input
                        className={inputCls}
                        type="number" step="0.1" placeholder={'W (in)'}
                        value={pa.printWidth ?? ''}
                        onChange={(e) => patchPrintArea(pa.id, { printWidth: num(e.target.value) })}
                      />
                      <input
                        className={inputCls}
                        type="number" step="0.1" placeholder={'H (in)'}
                        value={pa.printHeight ?? ''}
                        onChange={(e) => patchPrintArea(pa.id, { printHeight: num(e.target.value) })}
                      />
                    </div>
                    {!placedIds.has(pa.id) && imageSrc && (
                      <p className="mt-1.5 text-[10.5px] text-warning">Not placed on {activeView} — select, then drag on the image.</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* View canvas */}
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-1.5">
              {VIEW_KEYS.map((k) => (
                <button
                  key={k}
                  onClick={() => setActiveView(k)}
                  className={`rounded-full px-3 py-1 text-[11.5px] font-medium capitalize transition-colors ${activeView === k ? 'bg-text text-white' : 'bg-black/[0.05] text-muted hover:bg-black/[0.08]'}`}
                >
                  {k}
                  {Array.isArray(views[k].areas) && views[k].areas!.length > 0 && (
                    <span className="ml-1 opacity-65">· {views[k].areas!.length}</span>
                  )}
                </button>
              ))}
            </div>

            <input
              className={`${inputCls} mb-2`}
              placeholder="Image URL for this view (https://…)"
              value={view.imageUrl || ''}
              onChange={(e) => patchView(activeView, { imageUrl: e.target.value })}
            />

            {imageSrc ? (
              <ViewZoneCanvas
                imageSrc={imageSrc}
                areas={areas}
                printAreas={printAreas}
                selectedAreaId={selectedAreaId}
                onSelect={setSelectedAreaId}
                onChange={(next) => patchView(activeView, { areas: next })}
              />
            ) : (
              <div className="flex h-[300px] items-center justify-center rounded-[10px] border border-dashed border-border text-[12px] text-muted">
                No {activeView} image — paste an image URL above, or run “Fetch missing images” on the Products page.
              </div>
            )}

            {areas.length > 0 && (
              <p className="mt-1.5 font-mono text-[10px] text-subtle">
                {areas.map((a) => `${a.areaId}: ${a.x},${a.y} ${a.w}×${a.h}px`).join(' · ')}
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-divider pt-3">
          <Button variant="ghost" disabled={saving} onClick={onClose}>Cancel</Button>
          <Button disabled={saving || printAreas.length === 0} onClick={save}>
            {saving ? 'Saving…' : 'Save placement'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
